import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShoppingCart, Search, Trash2, RotateCcw, CheckCircle2, XCircle, Truck, ExternalLink } from "lucide-react";
import { fmtTRY, fmtDate } from "@/lib/format";
import { Link } from "react-router-dom";
import SupplierOrderDetailModal from "@/components/SupplierOrderDetailModal";

/* Tedarikçi Siparişleri sayfası
   Filtreler: durum, tedarikçi, tarih aralığı, arama (sipariş no / stok sku / not)
   Aksiyonlar: teslim al (received), iptal (cancelled), tekrar aç (open), sil
*/

const STATUS_LABELS = {
  open:      { label: "Açık",         cls: "bg-amber-100 text-amber-800 border-amber-300" },
  received:  { label: "Teslim Alındı", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  cancelled: { label: "İptal",        cls: "bg-neutral-200 text-neutral-600 border-neutral-300" },
};

export default function SupplierOrders() {
  const [rows, setRows] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [supplierFilter, setSupplierFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [q, setQ] = useState("");
  const [detailFor, setDetailFor] = useState(null); // seçili sipariş → detay modal

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: os }, { data: cs }] = await Promise.all([
        api.get("/supplier-orders"),
        api.get("/contacts"),
      ]);
      setRows(os || []);
      setContacts(cs || []);
    } catch (e) {
      toast.error("Siparişler yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const contactMap = useMemo(() => {
    const m = {};
    contacts.forEach((c) => { m[c.id] = c; });
    return m;
  }, [contacts]);
  const suppliers = useMemo(
    () => contacts.filter((c) => c.type === "supplier" || c.type === "both"),
    [contacts]
  );

  const filtered = useMemo(() => {
    const qs = q.trim().toLocaleLowerCase("tr");
    return rows.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (supplierFilter && r.supplier_contact_id !== supplierFilter) return false;
      if (dateFrom && (r.date || "") < dateFrom) return false;
      if (dateTo && (r.date || "") > dateTo) return false;
      if (qs) {
        const hay = [
          r.order_no, r.stock_sku, r.stock_name, r.notes, r.flute, r.quality,
          contactMap[r.supplier_contact_id]?.name,
          contactMap[r.customer_contact_id]?.name,
        ].filter(Boolean).join(" ").toLocaleLowerCase("tr");
        if (!hay.includes(qs)) return false;
      }
      return true;
    });
  }, [rows, statusFilter, supplierFilter, dateFrom, dateTo, q, contactMap]);

  const totals = useMemo(() => {
    let count = 0, amount = 0, profit = 0, sale = 0;
    filtered.forEach((r) => {
      count += 1;
      amount += Number(r.total_cost || 0);
      if (r.profit_amount != null) profit += Number(r.profit_amount);
      if (r.sale_price != null) sale += Number(r.sale_price);
    });
    return { count, amount, profit, sale };
  }, [filtered]);

  const counts = useMemo(() => {
    const c = { all: rows.length, open: 0, received: 0, cancelled: 0 };
    rows.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [rows]);

  const setStatus = async (o, next) => {
    try {
      await api.patch(`/supplier-orders/${o.id}`, { status: next });
      toast.success(next === "received" ? "Teslim alındı olarak işaretlendi"
                    : next === "cancelled" ? "Sipariş iptal edildi"
                    : "Sipariş yeniden açıldı");
      await load();
    } catch (e) {
      toast.error("Güncellenemedi: " + (e?.response?.data?.detail || e?.message));
    }
  };
  const remove = async (o) => {
    if (!window.confirm(`${o.order_no} silinsin mi? Bu işlem geri alınamaz.`)) return;
    try {
      await api.delete(`/supplier-orders/${o.id}`);
      toast.success("Silindi");
      await load();
    } catch (e) {
      toast.error("Silinemedi");
    }
  };
  const reset = () => {
    setStatusFilter("all"); setSupplierFilter(""); setDateFrom(""); setDateTo(""); setQ("");
  };

  return (
    <div className="space-y-6" data-testid="supplier-orders-page">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ShoppingCart size={20} />
            <h1 className="font-display text-3xl font-bold">Tedarikçi Siparişleri</h1>
          </div>
          <p className="text-sm text-neutral-500">
            Olumlu tekliflerden veya elle açılmış tüm tedarikçi siparişlerini tek yerden yönet.
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Filtreli Toplam</div>
          <div className="font-mono-num text-xl font-bold" data-testid="orders-total-amount">
            {fmtTRY(totals.amount)}
          </div>
          <div className="text-[10px] text-neutral-500 font-mono-num">
            {totals.count} sipariş
            {totals.sale > 0 && (
              <> · Satış: <span className="text-neutral-700">{fmtTRY(totals.sale)}</span></>
            )}
          </div>
          {totals.sale > 0 && totals.amount > 0 && (
            <div
              data-testid="orders-total-profit"
              className={`text-[11px] font-mono-num font-semibold ${
                totals.profit > 0 ? "text-emerald-700" : totals.profit < 0 ? "text-rose-700" : "text-neutral-500"
              }`}
            >
              Kâr: {totals.profit >= 0 ? "+" : ""}{fmtTRY(totals.profit)}
              {" · "}%{((totals.profit / totals.amount) * 100).toLocaleString("tr-TR", { maximumFractionDigits: 1 })}
              <span className="text-neutral-500 ml-1">(maliyet üzeri)</span>
            </div>
          )}
        </div>
      </header>

      {/* Durum sekmeleri */}
      <div className="flex flex-wrap items-center gap-2">
        {[
          { key: "all", label: "Tümü", cls: "bg-neutral-800" },
          { key: "open", label: "Açık", cls: "bg-amber-600" },
          { key: "received", label: "Teslim Alındı", cls: "bg-emerald-700" },
          { key: "cancelled", label: "İptal", cls: "bg-neutral-500" },
        ].map((t) => (
          <button
            key={t.key}
            data-testid={`orders-tab-${t.key}`}
            onClick={() => setStatusFilter(t.key)}
            className={`px-3 py-1.5 text-xs uppercase tracking-widest border-2 transition-colors ${
              statusFilter === t.key
                ? `${t.cls} text-white border-transparent`
                : "border-black/20 text-neutral-700 hover:border-black"
            }`}
          >
            {t.label}
            <span className={`ml-2 text-[10px] px-1 py-0.5 ${
              statusFilter === t.key ? "bg-white/20" : "bg-neutral-100"
            }`}>{counts[t.key] || 0}</span>
          </button>
        ))}
      </div>

      {/* Filtre satırı */}
      <div className="brutal-card p-3 flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[220px]">
          <div className="label-caps mb-1">Ara</div>
          <div className="relative">
            <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input
              data-testid="orders-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Sipariş no, stok, cari, not…"
              className="input pl-7"
            />
          </div>
        </div>
        <div>
          <div className="label-caps mb-1">Tedarikçi</div>
          <select
            data-testid="orders-supplier-filter"
            value={supplierFilter}
            onChange={(e) => setSupplierFilter(e.target.value)}
            className="input min-w-[180px] bg-white"
          >
            <option value="">Tümü</option>
            {suppliers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <div className="label-caps mb-1">Tarih Başlangıç</div>
          <input type="date" className="input" value={dateFrom}
                 data-testid="orders-date-from"
                 onChange={(e) => setDateFrom(e.target.value)} />
        </div>
        <div>
          <div className="label-caps mb-1">Tarih Bitiş</div>
          <input type="date" className="input" value={dateTo}
                 data-testid="orders-date-to"
                 onChange={(e) => setDateTo(e.target.value)} />
        </div>
        <button
          onClick={reset}
          data-testid="orders-reset-filters"
          className="border border-black/30 px-3 py-2 text-xs uppercase tracking-widest hover:bg-neutral-50 flex items-center gap-1.5"
        >
          <RotateCcw size={12} /> Sıfırla
        </button>
      </div>

      {/* Liste */}
      <div className="brutal-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b-2 border-black/60">
              <tr>
                <th className="text-left px-3 py-2 label-caps whitespace-nowrap">Sipariş No</th>
                <th className="text-left px-3 py-2 label-caps whitespace-nowrap">Tarih</th>
                <th className="text-left px-3 py-2 label-caps">Tedarikçi</th>
                <th className="text-left px-3 py-2 label-caps">Müşteri</th>
                <th className="text-left px-3 py-2 label-caps">Ürün</th>
                <th className="text-right px-3 py-2 label-caps">Adet</th>
                <th className="text-right px-3 py-2 label-caps">Toplam</th>
                <th className="text-right px-3 py-2 label-caps">Kâr</th>
                <th className="text-left px-3 py-2 label-caps">Durum</th>
                <th className="w-40 px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="text-center py-8 text-neutral-500">Yükleniyor…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={10} className="text-center py-10 text-neutral-500">
                  {rows.length === 0
                    ? "Henüz sipariş yok. Fiyat Hesapla → Teklif → Olumlu → Tedarikçiye Sipariş Aç ile oluşturabilirsiniz."
                    : "Filtreye uyan sipariş bulunamadı."}
                </td></tr>
              ) : filtered.map((o) => (
                <tr key={o.id}
                    className="border-b border-black/5 hover:bg-neutral-50 cursor-pointer"
                    data-testid={`order-row-${o.id}`}
                    onClick={() => setDetailFor(o)}>
                  <td className="px-3 py-2 font-mono-num font-semibold whitespace-nowrap">{o.order_no}</td>
                  <td className="px-3 py-2 font-mono-num whitespace-nowrap">{fmtDate(o.date)}</td>
                  <td className="px-3 py-2">
                    <div className="font-medium">{contactMap[o.supplier_contact_id]?.name || "—"}</div>
                    {o.delivery_date && (
                      <div className="text-[10px] text-neutral-500 flex items-center gap-1 mt-0.5">
                        <Truck size={10} /> Teslim: {fmtDate(o.delivery_date)}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-neutral-600">
                    {o.customer_contact_id ? (
                      <Link to={`/cari`} className="hover:underline inline-flex items-center gap-1">
                        {contactMap[o.customer_contact_id]?.name || "—"}
                        <ExternalLink size={10} />
                      </Link>
                    ) : <span className="text-neutral-400">—</span>}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {(o.stock_name || o.title) && (
                      <div className="font-semibold text-neutral-800 mb-0.5" data-testid={`order-product-name-${o.id}`}>
                        {o.stock_name || o.title}
                      </div>
                    )}
                    {(o.flute || o.quality) && (
                      <div className="text-neutral-600"><span className="font-semibold">{o.flute || "—"}</span>{o.quality && <> · {o.quality}</>}</div>
                    )}
                    {o.stock_sku && (
                      <div className="text-[10px] text-neutral-500 font-mono-num">{o.stock_sku}</div>
                    )}
                    {o.notes && (
                      <div className="text-[10px] text-neutral-500 truncate max-w-xs" title={o.notes}>
                        {o.notes}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-mono-num">{o.quantity}</td>
                  <td className="px-3 py-2 text-right font-mono-num font-semibold">{fmtTRY(o.total_cost)}</td>
                  <td className="px-3 py-2 text-right font-mono-num">
                    {o.profit_amount != null ? (
                      <div className={`${o.profit_amount > 0 ? "text-emerald-700" : o.profit_amount < 0 ? "text-rose-700" : "text-neutral-500"}`}>
                        <div className="font-semibold">{o.profit_amount >= 0 ? "+" : ""}{fmtTRY(o.profit_amount)}</div>
                        {o.profit_margin_pct != null && (
                          <div className="text-[10px]">%{o.profit_margin_pct.toLocaleString("tr-TR", { maximumFractionDigits: 1 })}</div>
                        )}
                      </div>
                    ) : <span className="text-neutral-300">—</span>}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex text-[10px] uppercase tracking-widest px-1.5 py-0.5 border ${STATUS_LABELS[o.status]?.cls || ""}`}>
                      {STATUS_LABELS[o.status]?.label || o.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="inline-flex items-center gap-1">
                      {o.status === "open" && (
                        <>
                          <button
                            onClick={() => setStatus(o, "received")}
                            data-testid={`order-mark-received-${o.id}`}
                            title="Teslim Alındı"
                            className="p-1.5 hover:bg-emerald-50 text-emerald-700"
                          >
                            <CheckCircle2 size={13} />
                          </button>
                          <button
                            onClick={() => setStatus(o, "cancelled")}
                            data-testid={`order-cancel-${o.id}`}
                            title="İptal Et"
                            className="p-1.5 hover:bg-rose-50 text-rose-600"
                          >
                            <XCircle size={13} />
                          </button>
                        </>
                      )}
                      {o.status !== "open" && (
                        <button
                          onClick={() => setStatus(o, "open")}
                          data-testid={`order-reopen-${o.id}`}
                          title="Yeniden Aç"
                          className="p-1.5 hover:bg-neutral-100 text-neutral-600"
                        >
                          <RotateCcw size={13} />
                        </button>
                      )}
                      <button
                        onClick={() => remove(o)}
                        data-testid={`order-remove-${o.id}`}
                        title="Sil"
                        className="p-1.5 hover:bg-rose-50 text-rose-600"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <style>{`.input{width:100%;border:1px solid rgba(0,0,0,0.2);padding:8px 10px;font-size:13px;outline:none;background:white}.input:focus{border-color:black}`}</style>

      {detailFor && (
        <SupplierOrderDetailModal
          order={detailFor}
          contactMap={contactMap}
          onClose={() => setDetailFor(null)}
          onChanged={() => { load(); }}
        />
      )}
    </div>
  );
}
